import { useEffect, useMemo, useState } from 'react';
import {
    analyzeCmmFiles,
    combineCmmFilesToXlsx,
} from '../../services/cmm-combine/combineCmmFiles.ts';
import type { CombineCmmAnalysis, CombineCmmWarning } from '../../services/cmm-combine/combineCmmFiles.ts';
import { saveFileWithPicker } from '../../utils/saveFile.ts';
import { FileInputButton } from '../../components/fileInputButton.tsx';
import { Dialog } from '../../components/dialog-portal/dialog.tsx';
import { NavBar } from '../../components/navBar.tsx';
import { PageTitle } from '../../components/pageTitle.tsx';

const ZEISS_CMM_FILE_EXTENSIONS = ['.xlsx', '.xlsm', '.xls'];

const WEB_TOOL_TABS = [{ key: 'cmm-combine', label: 'Zeiss CMM combine' }] as const;

type WebToolTabKey = (typeof WEB_TOOL_TABS)[number]['key'];
type SortDirection = 'asc' | 'desc';
type SelectedFileSortKey = 'fileName' | 'size' | 'includedPositions' | 'skippedPositions' | 'warnings';
type DetailSortKey = 'fileName' | 'isIncluded' | 'includedPositions' | 'skippedPositions' | 'warnings';

interface SortState<T extends string> {
    key: T;
    direction: SortDirection;
}

interface CombineResultState {
    summary: {
        fileCount: number;
        successCount: number;
        failedCount: number;
        partCount: number;
        positionCount: number;
        expectedPositionCount: number | null;
        unexpectedPositionFiles: number;
    };
    failures: {
        fileName: string;
        errorMessage: string;
    }[];
    fileDetails: {
        fileName: string;
        isIncluded: boolean;
        partCount: number;
        includedPositions: number;
        positionNames: string[];
        skippedPositions: number;
        skippedPositionNames: string[];
        skippedReason: string;
        warningMessages: string[];
    }[];
    warnings: CombineCmmWarning[];
    outputFilename: string;
    outputBlob: Blob;
}

export function WebToolsPage() {
    const [activeTab, setActiveTab] = useState<WebToolTabKey>('cmm-combine');

    return (
        <>
            <PageTitle title='Web Tools' />
            <NavBar />

            <div className='content web-tools__content'>
                <div className='web-tools__top-bar'>
                    {WEB_TOOL_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type='button'
                            className={`web-tools__tab ${activeTab === tab.key ? 'web-tools__tab--active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className='web-tools__panel'>{activeTab === 'cmm-combine' && <CmmCombineTool />}</div>
            </div>
        </>
    );
}

function CmmCombineTool() {
    const [files, setFiles] = useState<File[]>([]);
    const [isExpectedMeasurementsCheckEnabled, setIsExpectedMeasurementsCheckEnabled] = useState(false);
    const [expectedMeasurementsPerFileInput, setExpectedMeasurementsPerFileInput] = useState('');
    const [analysisState, setAnalysisState] = useState<CombineCmmAnalysis | null>(null);
    const [selectedFileSort, setSelectedFileSort] = useState<SortState<SelectedFileSortKey>>({ key: 'fileName', direction: 'asc' });
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisErrorText, setAnalysisErrorText] = useState<string | null>(null);
    const [isCombining, setIsCombining] = useState(false);
    const [errorText, setErrorText] = useState<string | null>(null);
    const [resultState, setResultState] = useState<CombineResultState | null>(null);
    const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
    const expectedMeasurementsPerFile = isExpectedMeasurementsCheckEnabled
        ? parseExpectedMeasurementsPerFile(expectedMeasurementsPerFileInput)
        : null;

    const analysisDetailsByFileName = useMemo(() => {
        return new Map(analysisState?.fileDetails.map((detail) => [detail.fileName, detail]) ?? []);
    }, [analysisState]);

    const sortedFiles = useMemo(() => {
        return sortSelectedFiles(files, analysisDetailsByFileName, selectedFileSort);
    }, [analysisDetailsByFileName, files, selectedFileSort]);

    useEffect(() => {
        if (files.length === 0) {
            setAnalysisState(null);
            setAnalysisErrorText(null);
            setIsAnalyzing(false);
            return;
        }

        let isCancelled = false;
        setIsAnalyzing(true);
        setAnalysisErrorText(null);

        analyzeCmmFiles(files, { expectedPositionsPerFile: expectedMeasurementsPerFile })
            .then((analysis) => {
                if (isCancelled) return;
                setAnalysisState(analysis);
            })
            .catch((error: any) => {
                if (isCancelled) return;
                setAnalysisState(null);
                setAnalysisErrorText(error?.message ?? 'Failed to check selected files.');
            })
            .finally(() => {
                if (isCancelled) return;
                setIsAnalyzing(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [files, expectedMeasurementsPerFile]);

    const handleFilesSelected = (selectedFiles: File[]) => {
        const deduplicated = new Map<string, File>();
        for (const file of [...files, ...selectedFiles]) {
            deduplicated.set(`${file.name}__${file.lastModified}__${file.size}`, file);
        }

        setFiles(Array.from(deduplicated.values()));
        setResultState(null);
        setErrorText(null);
        setAnalysisErrorText(null);
    };

    const handleCombine = async () => {
        if (files.length === 0 || isCombining) return;

        setIsCombining(true);
        setErrorText(null);

        try {
            const combineResult = await combineCmmFilesToXlsx(files, { expectedPositionsPerFile: expectedMeasurementsPerFile });
            const outputFilename = createSuggestedFilename();

            setResultState({
                summary: combineResult.summary,
                failures: combineResult.failures,
                fileDetails: combineResult.fileDetails,
                warnings: combineResult.warnings,
                outputFilename,
                outputBlob: combineResult.blob,
            });
            setIsResultDialogOpen(true);
        } catch (error: any) {
            setErrorText(error?.message ?? 'Failed to combine files.');
        } finally {
            setIsCombining(false);
        }
    };

    const handleDownload = async () => {
        if (!resultState) return;

        try {
            await saveFileWithPicker(resultState.outputFilename, resultState.outputBlob, 'xlsx');
        } catch (error: any) {
            setErrorText(error?.message ?? 'Failed to save combined file.');
        }
    };

    return (
        <div className='web-tools__tool'>
            <h2 style={{ marginTop: 0 }}>Zeiss CMM combine</h2>
            <p style={{ maxWidth: '900px' }}>
                Select multiple local Zeiss Calypso CMM report files (.xlsx) and combine all positions into one Excel file locally in your browser. No
                data is sent to the server.
                <br />
                <em>Note: this tool currently only supports Zeiss Calypso report files.</em>
            </p>

            <div className='button-bar' style={{ marginBottom: '12px' }}>
                <FileInputButton
                    allowedExtensions={ZEISS_CMM_FILE_EXTENSIONS}
                    onFilesSelected={handleFilesSelected}
                    multiple
                    content='Select Zeiss CMM files'
                />
                <button type='button' disabled={files.length === 0 || isCombining} onClick={handleCombine}>
                    {isCombining ? 'Combining...' : 'Combine'}
                </button>
                <button
                    type='button'
                    disabled={files.length === 0 || isCombining}
                    onClick={() => {
                        setFiles([]);
                        setResultState(null);
                        setErrorText(null);
                        setAnalysisErrorText(null);
                        setIsResultDialogOpen(false);
                    }}
                >
                    Clear
                </button>
            </div>

            <label className='web-tools__expected-count-toggle'>
                <input
                    type='checkbox'
                    checked={isExpectedMeasurementsCheckEnabled}
                    onChange={(e) => setIsExpectedMeasurementsCheckEnabled(e.target.checked)}
                />
                Check expected measurements count per file
                <input
                    type='number'
                    min='0'
                    step='1'
                    value={expectedMeasurementsPerFileInput}
                    disabled={!isExpectedMeasurementsCheckEnabled}
                    onChange={(e) => setExpectedMeasurementsPerFileInput(e.target.value)}
                />
            </label>

            <div style={{ marginBottom: '12px' }}>
                <strong>Selected files:</strong> {files.length}
            </div>

            {isAnalyzing && <p className='web-tools__analysis-status'>Checking selected files...</p>}
            {analysisErrorText && <p className='page-error'>{analysisErrorText}</p>}
            {analysisState && (
                <CmmAnalysisPanel
                    analysis={analysisState}
                    expectedMeasurementsPerFile={expectedMeasurementsPerFile}
                />
            )}

            {files.length > 0 && (
                <div className='web-tools__file-list'>
                    <table className='table__small-padding'>
                        <thead>
                            <tr>
                                <SortableTh
                                    label='File'
                                    sortKey='fileName'
                                    sortState={selectedFileSort}
                                    onSort={setSelectedFileSort}
                                    align='left'
                                />
                                <SortableTh
                                    label='Size (KB)'
                                    sortKey='size'
                                    sortState={selectedFileSort}
                                    onSort={setSelectedFileSort}
                                />
                                {analysisState && (
                                    <>
                                        <SortableTh
                                            label='Positions'
                                            sortKey='includedPositions'
                                            sortState={selectedFileSort}
                                            onSort={setSelectedFileSort}
                                        />
                                        <SortableTh
                                            label='Skipped rows'
                                            sortKey='skippedPositions'
                                            sortState={selectedFileSort}
                                            onSort={setSelectedFileSort}
                                        />
                                        <SortableTh
                                            label='Warnings'
                                            sortKey='warnings'
                                            sortState={selectedFileSort}
                                            onSort={setSelectedFileSort}
                                            align='left'
                                        />
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedFiles.map((file) => {
                                const detail = analysisDetailsByFileName.get(file.name);
                                return (
                                    <tr
                                        key={`${file.name}_${file.lastModified}_${file.size}`}
                                        className={detail && detail.warningMessages.length > 0 ? 'highlight--issue' : ''}
                                    >
                                        <td style={{ textAlign: 'left', paddingLeft: '8px' }}>{file.name}</td>
                                        <td>{(file.size / 1024).toFixed(1)}</td>
                                        {analysisState && (
                                            <>
                                                <td>{detail?.includedPositions ?? '-'}</td>
                                                <td>{detail?.skippedPositions ?? '-'}</td>
                                                <td style={{ textAlign: 'left', paddingLeft: '8px' }}>
                                                    {detail?.warningMessages.join(' ') || '-'}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {errorText && <p className='page-error'>{errorText}</p>}

            {resultState && (
                <div className='web-tools__result'>
                    <h3 style={{ marginTop: 0 }}>Result</h3>
                    <p style={{ margin: '0 0 8px 0' }}>
                        Combined file is ready: <strong>{resultState.outputFilename}</strong>.
                    </p>
                    <CmmObservationSummary
                        summary={resultState.summary}
                        failures={resultState.failures}
                        fileDetails={resultState.fileDetails}
                        expectedMeasurementsPerFile={expectedMeasurementsPerFile}
                    />
                    <div style={{ margin: '0 0 10px 0' }}>
                        <button type='button' onClick={handleDownload}>
                            Download combined file
                        </button>
                    </div>
                    <CmmFileDetailsTable fileDetails={resultState.fileDetails} />
                    {resultState.summary.failedCount > 0 && (
                        <>
                            <p style={{ margin: '0 0 6px 0' }}>Failed files ({resultState.summary.failedCount}):</p>
                            <ul style={{ marginTop: 0 }}>
                                {resultState.failures.map((failure) => (
                                    <li key={failure.fileName}>
                                        {failure.fileName}: {failure.errorMessage}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}

            {isResultDialogOpen && resultState && (
                <Dialog title='Zeiss CMM Combine Result' onClose={() => setIsResultDialogOpen(false)}>
                    <p style={{ marginTop: 0 }}>
                        Zeiss CMM combine result
                    </p>
                    <CmmObservationSummary
                        summary={resultState.summary}
                        failures={resultState.failures}
                        fileDetails={resultState.fileDetails}
                        expectedMeasurementsPerFile={expectedMeasurementsPerFile}
                    />
                    <CmmFileDetailsTable fileDetails={resultState.fileDetails} />
                    <p style={{ marginBottom: 0, marginTop: '10px' }}>
                        Close this dialog and use <strong>Download combined file</strong> in the result area below.
                    </p>
                </Dialog>
            )}
        </div>
    );
}

function CmmAnalysisPanel({
    analysis,
    expectedMeasurementsPerFile,
}: {
    analysis: CombineCmmAnalysis;
    expectedMeasurementsPerFile: number | null;
}) {
    return (
        <div className={`web-tools__analysis ${analysis.warnings.length > 0 ? 'web-tools__analysis--warning' : ''}`}>
            <CmmObservationSummary
                summary={analysis.summary}
                failures={analysis.failures}
                fileDetails={analysis.fileDetails}
                expectedMeasurementsPerFile={expectedMeasurementsPerFile}
            />
        </div>
    );
}

function CmmObservationSummary({
    summary,
    failures,
    fileDetails,
    expectedMeasurementsPerFile,
}: {
    summary: CombineResultState['summary'];
    failures: CombineResultState['failures'];
    fileDetails: CombineResultState['fileDetails'];
    expectedMeasurementsPerFile: number | null;
}) {
    const skippedInvalidRows = fileDetails.reduce((sum, detail) => sum + detail.skippedPositions, 0);
    const filesWithSkippedRows = fileDetails.filter((detail) => detail.skippedPositions > 0).length;
    const differentPositionNames = countDifferentPositionNames(fileDetails);
    const expectedMeasurementsPerFileDisplay = expectedMeasurementsPerFile
        ?? (summary.expectedPositionCount !== null && summary.fileCount > 0 ? summary.expectedPositionCount / summary.fileCount : null);
    const hasIrregularities = summary.failedCount > 0
        || summary.unexpectedPositionFiles > 0
        || skippedInvalidRows > 0;

    return (
        <div className='web-tools__observations'>
            <p>
                Parsed <strong>{summary.successCount}/{summary.fileCount}</strong> files, total <strong>{summary.positionCount}</strong>{' '}
                measurements, <strong>{differentPositionNames}</strong> different position names.
                {summary.failedCount > 0 && <> Files not parsed: {formatFailureNames(failures)}.</>}
            </p>
            {summary.expectedPositionCount !== null && (
                <p>
                    Expected-count check: expected total of <strong>{summary.expectedPositionCount}</strong> measurements.
                </p>
            )}
            {expectedMeasurementsPerFileDisplay !== null && summary.unexpectedPositionFiles > 0 && (
                <p>
                    Position count mismatch: {summary.unexpectedPositionFiles} files do not match the expected{' '}
                    {expectedMeasurementsPerFileDisplay} positions per file expectation.
                </p>
            )}
            {skippedInvalidRows > 0 && (
                <p>
                    Skipped invalid position rows: total of {skippedInvalidRows} rows in {filesWithSkippedRows} files{' '}
                    {formatSkippedPositionNames(fileDetails)}.
                </p>
            )}
            {failures.length > 0 && (
                <ul className='web-tools__failed-files'>
                    {failures.map((failure) => (
                        <li key={failure.fileName}>
                            {failure.fileName}: {failure.errorMessage}
                        </li>
                    ))}
                </ul>
            )}
            {!hasIrregularities && <p className='web-tools__warnings-ok'>No irregularities found.</p>}
        </div>
    );
}

function CmmFileDetailsTable({ fileDetails }: { fileDetails: CombineResultState['fileDetails'] }) {
    const [sortState, setSortState] = useState<SortState<DetailSortKey>>({ key: 'fileName', direction: 'asc' });
    const sortedDetails = useMemo(() => sortFileDetails(fileDetails, sortState), [fileDetails, sortState]);

    return (
        <div className='web-tools__file-list' style={{ marginBottom: '10px' }}>
            <table className='table__small-padding'>
                <thead>
                    <tr>
                        <SortableTh label='File' sortKey='fileName' sortState={sortState} onSort={setSortState} align='left' />
                        <SortableTh label='Included' sortKey='isIncluded' sortState={sortState} onSort={setSortState} />
                        <SortableTh label='Positions' sortKey='includedPositions' sortState={sortState} onSort={setSortState} />
                        <SortableTh label='Skipped positions' sortKey='skippedPositions' sortState={sortState} onSort={setSortState} />
                        <SortableTh label='Reason' sortKey='warnings' sortState={sortState} onSort={setSortState} align='left' />
                    </tr>
                </thead>
                <tbody>
                    {sortedDetails.map((detail) => (
                        <tr key={detail.fileName} className={detail.warningMessages.length > 0 ? 'highlight--issue' : ''}>
                            <td style={{ textAlign: 'left', paddingLeft: '8px' }}>{detail.fileName}</td>
                            <td>{detail.isIncluded ? 'Yes' : 'No'}</td>
                            <td>{detail.includedPositions}</td>
                            <td>{detail.skippedPositions}</td>
                            <td style={{ textAlign: 'left', paddingLeft: '8px' }}>
                                {detail.warningMessages.join(' ') || detail.skippedReason || '-'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SortableTh<T extends string>({
    label,
    sortKey,
    sortState,
    onSort,
    align = 'center',
}: {
    label: string;
    sortKey: T;
    sortState: SortState<T>;
    onSort: (sortState: SortState<T>) => void;
    align?: 'left' | 'center';
}) {
    const isActive = sortState.key === sortKey;
    const nextDirection: SortDirection = isActive && sortState.direction === 'asc' ? 'desc' : 'asc';

    return (
        <th style={{ textAlign: align, paddingLeft: align === 'left' ? '8px' : undefined }}>
            <button
                type='button'
                className='web-tools__sort-button'
                style={{ justifyContent: align === 'left' ? 'flex-start' : 'center' }}
                onClick={() => onSort({ key: sortKey, direction: nextDirection })}
            >
                <span>{label}</span>
                <span className={`web-tools__sort-indicator ${isActive ? '' : 'web-tools__sort-indicator--inactive'}`}>
                    {isActive && sortState.direction === 'desc' ? 'v' : '^'}
                </span>
            </button>
        </th>
    );
}

function createSuggestedFilename() {
    const now = new Date();
    const timestamp =
        [now.getFullYear().toString(), pad2(now.getMonth() + 1), pad2(now.getDate())].join('') +
        '_' +
        [pad2(now.getHours()), pad2(now.getMinutes()), pad2(now.getSeconds())].join('');

    return `cmm-combined-${timestamp}.xlsx`;
}

function pad2(value: number) {
    return value.toString().padStart(2, '0');
}

function parseExpectedMeasurementsPerFile(value: string): number | null {
    if (value.trim() === '') return null;

    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return Math.floor(parsed);
}

function sortSelectedFiles(
    files: File[],
    analysisDetailsByFileName: Map<string, CombineResultState['fileDetails'][number]>,
    sortState: SortState<SelectedFileSortKey>
): File[] {
    return [...files].sort((a, b) => {
        const detailA = analysisDetailsByFileName.get(a.name);
        const detailB = analysisDetailsByFileName.get(b.name);
        const result = compareValues(
            getSelectedFileSortValue(a, detailA, sortState.key),
            getSelectedFileSortValue(b, detailB, sortState.key)
        );
        return sortState.direction === 'asc' ? result : -result;
    });
}

function getSelectedFileSortValue(
    file: File,
    detail: CombineResultState['fileDetails'][number] | undefined,
    key: SelectedFileSortKey
) {
    if (key === 'fileName') return file.name;
    if (key === 'size') return file.size;
    if (key === 'warnings') return detail?.warningMessages.length ?? 0;
    return detail?.[key] ?? -1;
}

function sortFileDetails(
    fileDetails: CombineResultState['fileDetails'],
    sortState: SortState<DetailSortKey>
): CombineResultState['fileDetails'] {
    return [...fileDetails].sort((a, b) => {
        const result = compareValues(getFileDetailSortValue(a, sortState.key), getFileDetailSortValue(b, sortState.key));
        return sortState.direction === 'asc' ? result : -result;
    });
}

function getFileDetailSortValue(detail: CombineResultState['fileDetails'][number], key: DetailSortKey) {
    if (key === 'warnings') return detail.warningMessages.length;
    if (key === 'isIncluded') return detail.isIncluded ? 1 : 0;
    return detail[key];
}

function compareValues(a: string | number | boolean, b: string | number | boolean): number {
    if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b);
    if (a === b) return 0;
    return a > b ? 1 : -1;
}

function formatFailureNames(failures: CombineResultState['failures']): string {
    const visibleFailures = failures.slice(0, 5).map((failure) => failure.fileName);
    const hiddenCount = failures.length - visibleFailures.length;
    return hiddenCount > 0
        ? `${visibleFailures.join(', ')} and ${hiddenCount} more`
        : visibleFailures.join(', ');
}

function formatSkippedPositionNames(fileDetails: CombineResultState['fileDetails']): string {
    const names = fileDetails.flatMap((detail) => detail.skippedPositionNames);
    if (names.length === 0) return '';

    const uniqueNames = Array.from(new Set(names));
    const visibleNames = uniqueNames.slice(0, 8);
    const hiddenCount = uniqueNames.length - visibleNames.length;
    const namesText = hiddenCount > 0
        ? `${visibleNames.join(', ')} and ${hiddenCount} more`
        : visibleNames.join(', ');
    return `(${namesText})`;
}

function countDifferentPositionNames(fileDetails: CombineResultState['fileDetails']): number {
    return new Set(fileDetails.flatMap((detail) => detail.positionNames)).size;
}
